import QRCode from 'qrcode';

export const GO_QR_LOGO_SRC = '/go-qr-logo.png';
export const GO_QR_SIZE = 1024;
export const GO_QR_LOGO_STORAGE_KEY = 'lccto.goQr.logoSrc';
export const GO_QR_STYLE_STORAGE_KEY = 'lccto.goQr.style';
export const GO_QR_DEFAULT_DARK = '#0c1424';
export const GO_QR_DEFAULT_LOGO_SCALE = 0.2;
export const GO_QR_LOGO_SCALE_MIN = 0.12;
export const GO_QR_LOGO_SCALE_MAX = 0.32;

export const GO_QR_INK_SWATCHES = [
  '#0c1424',
  '#111111',
  '#334155',
  '#047857',
  '#1d4ed8',
] as const;

export type GoQrStyle = {
  dark: string;
  logoScale: number;
};

const HEX = /^#([0-9a-f]{6})$/i;

export function clampLogoScale(scale: number): number {
  if (!Number.isFinite(scale)) return GO_QR_DEFAULT_LOGO_SCALE;
  return Math.min(GO_QR_LOGO_SCALE_MAX, Math.max(GO_QR_LOGO_SCALE_MIN, scale));
}

export function normalizeQrInk(color: string): string {
  const value = color.trim();
  return HEX.test(value) ? value.toLowerCase() : GO_QR_DEFAULT_DARK;
}

export function readSavedGoQrStyle(): GoQrStyle {
  try {
    const raw = localStorage.getItem(GO_QR_STYLE_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<GoQrStyle>;
      return {
        dark: normalizeQrInk(parsed.dark ?? GO_QR_DEFAULT_DARK),
        logoScale: clampLogoScale(parsed.logoScale ?? GO_QR_DEFAULT_LOGO_SCALE),
      };
    }
  } catch {
    /* ignore */
  }
  return { dark: GO_QR_DEFAULT_DARK, logoScale: GO_QR_DEFAULT_LOGO_SCALE };
}

export function saveGoQrStyle(style: GoQrStyle) {
  localStorage.setItem(
    GO_QR_STYLE_STORAGE_KEY,
    JSON.stringify({
      dark: normalizeQrInk(style.dark),
      logoScale: clampLogoScale(style.logoScale),
    }),
  );
}

export type GoQrLogoChoice = {
  src: string;
  label: string;
};

export function readSavedGoQrLogo(): GoQrLogoChoice {
  try {
    const raw = localStorage.getItem(GO_QR_LOGO_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as GoQrLogoChoice;
      if (parsed?.src && parsed.label) return parsed;
    }
  } catch {
    /* ignore */
  }
  return { src: GO_QR_LOGO_SRC, label: 'Low Code CTO' };
}

export function saveGoQrLogo(choice: GoQrLogoChoice) {
  localStorage.setItem(GO_QR_LOGO_STORAGE_KEY, JSON.stringify(choice));
}

function isSameOriginSrc(src: string): boolean {
  return src.startsWith('data:') || src.startsWith('blob:') || src.startsWith('/');
}

function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Could not load ${src}`));
    img.src = src;
  });
}

/** Decode a remote CDN mark via fetch so a prior <img> cache miss without CORS cannot taint the canvas. */
async function loadLogoSource(src: string): Promise<CanvasImageSource> {
  if (isSameOriginSrc(src)) return loadImageElement(src);
  const res = await fetch(src, { mode: 'cors', credentials: 'omit', cache: 'reload' });
  if (!res.ok) throw new Error(`Could not load mark (${res.status})`);
  return createImageBitmap(await res.blob());
}

function fillRoundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
  ctx.fill();
}

export async function renderGoQrCanvas(
  url: string,
  opts?: { withLogo?: boolean; logoSrc?: string; dark?: string; logoScale?: number },
): Promise<HTMLCanvasElement> {
  const canvas = document.createElement('canvas');
  await QRCode.toCanvas(canvas, url, {
    width: GO_QR_SIZE,
    margin: 2,
    errorCorrectionLevel: 'H',
    color: { dark: normalizeQrInk(opts?.dark ?? GO_QR_DEFAULT_DARK), light: '#ffffff' },
  });

  if (opts?.withLogo === false) return canvas;

  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  try {
    const logo = await loadLogoSource(opts?.logoSrc || GO_QR_LOGO_SRC);
    const box = Math.round(GO_QR_SIZE * clampLogoScale(opts?.logoScale ?? GO_QR_DEFAULT_LOGO_SCALE));
    const pad = Math.round(box * 0.14);
    const x = (GO_QR_SIZE - box) / 2;
    const y = (GO_QR_SIZE - box) / 2;
    ctx.fillStyle = '#ffffff';
    fillRoundRect(ctx, x - pad, y - pad, box + pad * 2, box + pad * 2, 18);
    ctx.drawImage(logo, x, y, box, box);
    if (typeof ImageBitmap !== 'undefined' && logo instanceof ImageBitmap) logo.close();
  } catch (error) {
    if (opts?.logoSrc && opts.logoSrc !== GO_QR_LOGO_SRC) throw error;
  }

  return canvas;
}

export function downloadCanvasPng(canvas: HTMLCanvasElement, filename: string) {
  const href = canvas.toDataURL('image/png');
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  a.click();
}
